import { useState, useEffect, useMemo } from 'react'
import { Button } from '@components/primitives/Button.js'
import { Modal } from '@components/primitives/Modal.js'
import { Input } from '@components/primitives/Input.js'
import { MoneyInput } from '@components/primitives/MoneyInput.js'
import { createId } from '@utils/createId.js'
import type { OrcamentoMaterial } from '@services/db.js'

type Props = {
  open: boolean
  grupoId?: string
  materialNome?: string
  onClose: () => void
  onSave: (data: Omit<OrcamentoMaterial, 'id' | 'criadoEm' | 'atualizadoEm'>) => Promise<void>
}

export function OrcamentoFormModal({ open, grupoId, materialNome, onClose, onSave }: Props) {
  const [material, setMaterial] = useState('')
  const [fornecedor, setFornecedor] = useState('')
  const [descricao, setDescricao] = useState('')
  const [valorTotal, setValorTotal] = useState(0)
  const [data, setData] = useState(new Date().toISOString().slice(0, 10))
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')

  const isNewGrupo = !grupoId

  useEffect(() => {
    if (!open) return
    setMaterial(materialNome ?? '')
    setFornecedor('')
    setDescricao('')
    setValorTotal(0)
    setData(new Date().toISOString().slice(0, 10))
    setSaveError('')
  }, [open, materialNome, grupoId])

  const materialFinal = useMemo(
    () => (grupoId ? (materialNome ?? material) : material).trim(),
    [grupoId, materialNome, material],
  )

  const canSave = Boolean(materialFinal && fornecedor.trim() && valorTotal > 0)

  async function handleSave(e?: React.FormEvent) {
    e?.preventDefault()
    if (!canSave || saving) return
    setSaving(true)
    setSaveError('')
    try {
      await onSave({
        material: materialFinal,
        fornecedor: fornecedor.trim(),
        descricao: descricao.trim(),
        valorTotal,
        data,
        status: 'cotado',
        grupoId: grupoId ?? createId(),
        categoria: 'Materiais',
      })
      onClose()
    } catch (err) {
      console.error(err)
      setSaveError('Não foi possível salvar. Tente novamente.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      open={open}
      title={isNewGrupo ? 'Novo orçamento de material' : 'Nova cotação'}
      size='md'
      onClose={onClose}
      actions={
        <>
          <Button variant='ghost' size='sm' onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button
            type='submit'
            form='orcamento-mat-form'
            size='sm'
            loading={saving}
            disabled={!canSave || saving}
          >
            Salvar
          </Button>
        </>
      }
    >
      <form id='orcamento-mat-form' className='cf3-mat-form' onSubmit={handleSave} noValidate>
        {isNewGrupo ? (
          <Input
            label='Material / item'
            value={material}
            onChange={(e) => setMaterial(e.target.value)}
            placeholder='Ex: Tijolos cerâmicos 9x19x19'
            required
          />
        ) : (
          <p className='cf3-mat-form__material-label'>
            Material: <strong>{materialNome}</strong>
          </p>
        )}
        <Input
          label='Fornecedor'
          value={fornecedor}
          onChange={(e) => setFornecedor(e.target.value)}
          placeholder='Nome do fornecedor'
          required
        />
        <MoneyInput label='Valor total' value={valorTotal} onChange={setValorTotal} required />
        <Input
          label='Data da cotação'
          type='date'
          value={data}
          onChange={(e) => setData(e.target.value)}
        />
        <Input
          label='Descrição (opcional)'
          value={descricao}
          onChange={(e) => setDescricao(e.target.value)}
          placeholder='Quantidade, marca, condições…'
        />
        {saveError && (
          <p className='cf3-mat-form__error' role='alert'>
            {saveError}
          </p>
        )}
      </form>
    </Modal>
  )
}
